// Default imports
import React, { useEffect, useState } from "react";
import { Row, Col, Container } from "react-bootstrap";

import {
    FacebookShareButton,
    LinkedinShareButton,
    TwitterShareButton,
    EmailShareButton,
} from "react-share";

import IconFb from "../icons/icon-facebook.svg";
import IconIn from "../icons/icon-linkedin.svg";
import IconTw from "../icons/icon-twitter.svg";
import IconMa from "../icons/icon-envelope.svg";
import slugifyAnchor from "../helpers/slugifyAnchor";
import ButtonText from "./ButtonText";
import ContentBlockError from "./ContentBlockError";
import ContentDefinitionBlock from "./ContentDefinitionBlock";
import ContentQuoteBlock from "./ContentQuoteBlock";
import ContentTakeawaysBlock from "./ContentTakeawaysBlock";
import TableOfContentsBlock from "./TableOfContentsBlock";

const addScrollableTables = (content = "") =>
    content
        .replace(/<table\b/g, '<div class="article__table-scroll"><table')
        .replace(/<\/table>/g, "</table></div>");

const shortcodePattern =
    /(?:\[|&lbrack;|&#0*91;|&#x0*5b;|<|&lt;)(table\\?_of\\?_contents|content\\?_definition|content\\?_quote|content\\?_takeaways)\b([\s\S]*?)(?:\]|&rbrack;|&#0*93;|&#x0*5d;|>|&gt;)/gi;
const contentAnchorPattern =
    /(?:\[|&lbrack;|&#0*91;|&#x0*5b;|<|&lt;)(content\\?_anchor)\b([\s\S]*?)(?:\]|&rbrack;|&#0*93;|&#x0*5d;|>|&gt;)/gi;

const getShortcodeId = (attributes = "") => {
    const normalizedAttributes = attributes
        .replace(
            /&quot;|&ldquo;|&rdquo;|&#0*34;|&#0*822[01];|&#x0*201[cd];/gi,
            '"',
        )
        .replace(
            /&apos;|&lsquo;|&rsquo;|&#0*39;|&#0*821[67];|&#x0*201[89];/gi,
            "'",
        )
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/&nbsp;|&#x0*20;/gi, " ")
        .replace(/\\_/g, "_");
    const idMatch = normalizedAttributes.match(
        /\bid\s*=\s*(?:["']([^"']+)["']|([^\s>]+))/i,
    );

    return (idMatch?.[1] || idMatch?.[2] || "").trim();
};

const addContentAnchors = (content = "") =>
    content.replace(contentAnchorPattern, (shortcode, name, attributes) => {
        const anchorId = slugifyAnchor(getShortcodeId(attributes));

        return anchorId
            ? `<span class="article__content-anchor" id="${anchorId}"></span>`
            : shortcode;
    });

const prepareArticleHtml = (content = "") =>
    addScrollableTables(addContentAnchors(content));

const ArticleContent = ({
    content = "",
    tableOfContentsBlocks = [],
    contentDefinitionBlocks = [],
    contentQuoteBlocks = [],
    contentTakeawaysBlocks = [],
}) => {
    const blockTypes = {
        table_of_contents: {
            blocks: tableOfContentsBlocks || [],
            Component: TableOfContentsBlock,
            isValid: (block) =>
                block?.sections?.some(
                    (section) =>
                        section?.header?.trim() &&
                        section?.subsections?.some(
                            (subsection) =>
                                subsection?.header?.trim() &&
                                subsection?.anchorId?.trim(),
                        ),
                ),
        },
        content_definition: {
            blocks: contentDefinitionBlocks || [],
            Component: ContentDefinitionBlock,
            isValid: (block) => block?.title?.trim() && block?.text?.trim(),
        },
        content_quote: {
            blocks: contentQuoteBlocks || [],
            Component: ContentQuoteBlock,
            isValid: (block) => block?.quote?.trim(),
        },
        content_takeaways: {
            blocks: contentTakeawaysBlocks || [],
            Component: ContentTakeawaysBlock,
            isValid: (block) =>
                block?.title?.trim() &&
                block?.items?.some((item) => item?.text?.trim()),
        },
    };
    const articleHtml = content || "";
    const contentParts = [];
    let lastIndex = 0;
    let match;

    shortcodePattern.lastIndex = 0;

    while ((match = shortcodePattern.exec(articleHtml)) !== null) {
        const html = articleHtml.slice(lastIndex, match.index);
        const shortcode = match[1].replace(/\\_/g, "_").toLowerCase();
        const blockId = getShortcodeId(match[2]);
        const { blocks, Component, isValid } = blockTypes[shortcode];
        const block = blocks.find((item) => item?.blockId === blockId);

        if (html) {
            contentParts.push(
                <div
                    className="article__content"
                    key={`content-${lastIndex}`}
                    dangerouslySetInnerHTML={{
                        __html: prepareArticleHtml(html),
                    }}
                />,
            );
        }

        if (!blockId) {
            contentParts.push(
                <ContentBlockError
                    shortcode={shortcode}
                    reason="missing or invalid id"
                    key={`block-error-${match.index}`}
                />,
            );
        } else if (!block) {
            contentParts.push(
                <ContentBlockError
                    shortcode={shortcode}
                    blockId={blockId}
                    reason="matching ACF block was not found"
                    key={`block-error-${match.index}`}
                />,
            );
        } else if (!isValid(block)) {
            contentParts.push(
                <ContentBlockError
                    shortcode={shortcode}
                    blockId={blockId}
                    reason="required ACF fields are empty"
                    key={`block-error-${match.index}`}
                />,
            );
        } else {
            contentParts.push(
                <Component
                    block={block}
                    key={`${shortcode}-${blockId}-${match.index}`}
                />,
            );
        }

        lastIndex = match.index + match[0].length;
    }

    const remainingHtml = articleHtml.slice(lastIndex);

    if (remainingHtml) {
        contentParts.push(
            <div
                className="article__content"
                key={`content-${lastIndex}`}
                dangerouslySetInnerHTML={{
                    __html: prepareArticleHtml(remainingHtml),
                }}
            />,
        );
    }

    return contentParts;
};

const Article = ({ title, categories, content, date, postsRichContent }) => {
    const [currentUrl, setCurrentUrl] = useState("");

    useEffect(() => {
        setCurrentUrl(window.location.href);
    }, []);

    return (
        <div>
            {/* Article */}
            <Container className="article">
                <Row>
                    <Col xs="24">
                        <div className="article__label">
                            <div className="article__title">
                                {categories.nodes[0].name}
                            </div>
                            <span>|</span>
                            <div className="article__date">{date}</div>
                        </div>
                        <h1 className="article__header">{title}</h1>
                        {postsRichContent?.author && (
                            <p className="article__author">
                                By {postsRichContent.author}
                            </p>
                        )}
                        <p className="article__social-media">
                            <TwitterShareButton
                                className="article__icon-box"
                                url={currentUrl}
                                title={title}
                            >
                                <IconTw className="article__icon" />
                            </TwitterShareButton>

                            <LinkedinShareButton
                                className="article__icon-box"
                                url={currentUrl}
                                title={title}
                            >
                                <IconIn className="article__icon" />
                            </LinkedinShareButton>

                            <FacebookShareButton
                                className="article__icon-box"
                                url={currentUrl}
                            >
                                <IconFb className="article__icon" />
                            </FacebookShareButton>

                            <EmailShareButton
                                className="article__icon-box"
                                url={currentUrl}
                                subject={title}
                                body="Check this article!"
                                separator=" - "
                                // Fix for opening in current window
                                onClick={() => {}}
                                openShareDialogOnClick={true}
                            >
                                <IconMa className="article__icon" />
                            </EmailShareButton>
                        </p>
                        <ArticleContent
                            content={content}
                            tableOfContentsBlocks={
                                postsRichContent?.tableOfContentsBlocks
                            }
                            contentDefinitionBlocks={
                                postsRichContent?.contentDefinitionBlocks
                            }
                            contentQuoteBlocks={
                                postsRichContent?.contentQuoteBlocks
                            }
                            contentTakeawaysBlocks={
                                postsRichContent?.contentTakeawaysBlocks
                            }
                        />
                        <div className="article__links">
                            {postsRichContent &&
                                postsRichContent.additionalLinks?.map(
                                    ({ type, text, link, file }, index) => {
                                        let btnLink = "";
                                        if (type === "file") {
                                            btnLink = {
                                                url: file.localFile.publicURL,
                                                title: text,
                                                target: "_blank",
                                            };
                                        } else {
                                            btnLink = link;
                                        }
                                        return (
                                            <ButtonText
                                                key={`article-links-item-${index}`}
                                                className="article__links-item"
                                                variant="red"
                                                {...btnLink}
                                            >
                                                {btnLink?.title}
                                            </ButtonText>
                                        );
                                    },
                                )}
                        </div>
                    </Col>
                    <div className="article__footer"></div>
                </Row>
            </Container>
        </div>
    );
};

export default Article;
